/**
 * Landing Page V2 - Boutique Stays Section
 */

'use client';

import React from 'react';
import {
  typographyColors,
  backgroundColors,
  spacing,
  fontPrimary,
} from '@/design-system/utils';
import Image from 'next/image';
import Link from 'next/link';
import { useBoutiqueStays } from '@/hooks/useExperiences';
import type { Experience } from '@/services/experienceService';
import { getExperienceImage } from '@/services/experienceService';
import { ScrollReveal } from './ScrollReveal';
import { useCurrency } from '@/hooks/useCurrency';
import { useConvertedPrice } from '@/hooks/useCurrencyPrice';
import { getCurrencyCode } from '@/utils/currencyHelpers';

// Fallback stays if API fails (not used - sections hide if no API data)
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const fallbackStays: Experience[] = [
  {
    _id: 'stay-1',
    name: 'Munnar Tea Hills',
    title: 'Munnar Tea Hills Nature Retreats',
    slug: 'munnar-tea-hills',
    code: 'STAY-001',
    type: 'stay_experience',
    category: {
      title: 'Nature Retreat',
      description: 'Mountain stay experience',
      _id: 'cat-stay-1',
    },
    location: {
      _id: 'loc-stay-1',
      name: 'Munnar',
      state: 'Kerala',
      city: 'Munnar',
    },
    imageUrl: ['/assets/images/location/image-1.png'],
    price: 8000,
    currencySymbol: '₹',
    averageRating: 4.7,
    reviewCount: 85,
    isActive: true,
    curated: true,
    createdAt: new Date().toISOString(),
  },
  {
    _id: 'stay-2',
    name: 'Manali Mountain Cabins',
    title: 'Snow Cabins Under Manali Mountain Skies',
    slug: 'manali-cabins',
    code: 'STAY-002',
    type: 'stay_experience',
    category: {
      title: 'Mountain Cabin',
      description: 'Snow cabin experience',
      _id: 'cat-stay-2',
    },
    location: {
      _id: 'loc-stay-2',
      name: 'Manali',
      state: 'Himachal Pradesh',
      city: 'Manali',
    },
    imageUrl: ['/assets/images/location/image-2.png'],
    price: 7000,
    currencySymbol: '₹',
    averageRating: 4.8,
    reviewCount: 92,
    isActive: true,
    curated: true,
    createdAt: new Date().toISOString(),
  },
  {
    _id: 'stay-3',
    name: 'Jaipur Heritage Palace',
    title: 'Heritage Nights Inside Jaipur Palaces',
    slug: 'jaipur-palace',
    code: 'STAY-003',
    type: 'stay_experience',
    category: {
      title: 'Heritage Palace',
      description: 'Royal palace stay',
      _id: 'cat-stay-3',
    },
    location: {
      _id: 'loc-stay-3',
      name: 'Jaipur',
      state: 'Rajasthan',
      city: 'Jaipur',
    },
    imageUrl: ['/assets/images/location/image-3.png'],
    price: 6000,
    currencySymbol: '₹',
    averageRating: 4.9,
    reviewCount: 110,
    isActive: true,
    curated: true,
    createdAt: new Date().toISOString(),
  },
  {
    _id: 'stay-4',
    name: 'Goa Beach Resort',
    title: 'Beachside Stays, Sunlit Goa Escapes',
    slug: 'goa-beach',
    code: 'STAY-004',
    type: 'stay_experience',
    category: {
      title: 'Beach Resort',
      description: 'Coastal stay experience',
      _id: 'cat-stay-4',
    },
    location: {
      _id: 'loc-stay-4',
      name: 'Goa',
      state: 'Goa',
      city: 'Calangute',
    },
    imageUrl: ['/assets/images/location/image-4.png'],
    price: 12000,
    currencySymbol: '₹',
    averageRating: 4.6,
    reviewCount: 78,
    isActive: true,
    curated: true,
    createdAt: new Date().toISOString(),
  },
  {
    _id: 'stay-5',
    name: 'Coorg Coffee Estate',
    title: 'Coffee Estate Stays in Coorg Hills',
    slug: 'coorg-coffee-estate',
    code: 'STAY-005',
    type: 'stay_experience',
    category: {
      title: 'Coffee Estate',
      description: 'Coffee plantation stay',
      _id: 'cat-stay-5',
    },
    location: {
      _id: 'loc-stay-5',
      name: 'Coorg',
      state: 'Karnataka',
      city: 'Madikeri',
    },
    imageUrl: ['/assets/images/location/image-5.png'],
    price: 9000,
    currencySymbol: '₹',
    averageRating: 4.7,
    reviewCount: 95,
    isActive: true,
    curated: true,
    createdAt: new Date().toISOString(),
  },
  {
    _id: 'stay-6',
    name: 'Udaipur Lake Palace',
    title: 'Royal Lake Palace Experience in Udaipur',
    slug: 'udaipur-lake-palace',
    code: 'STAY-006',
    type: 'stay_experience',
    category: {
      title: 'Lake Palace',
      description: 'Royal palace on lake',
      _id: 'cat-stay-6',
    },
    location: {
      _id: 'loc-stay-6',
      name: 'Udaipur',
      state: 'Rajasthan',
      city: 'Udaipur',
    },
    imageUrl: ['/assets/images/location/image-6.png'],
    price: 18000,
    currencySymbol: '₹',
    averageRating: 4.9,
    reviewCount: 112,
    isActive: true,
    curated: true,
    createdAt: new Date().toISOString(),
  },
  {
    _id: 'stay-7',
    name: 'Rishikesh Yoga Retreat',
    title: 'Riverside Yoga Retreat in Rishikesh',
    slug: 'rishikesh-yoga-retreat',
    code: 'STAY-007',
    type: 'stay_experience',
    category: {
      title: 'Yoga Retreat',
      description: 'Wellness retreat',
      _id: 'cat-stay-7',
    },
    location: {
      _id: 'loc-stay-7',
      name: 'Rishikesh',
      state: 'Uttarakhand',
      city: 'Rishikesh',
    },
    imageUrl: ['/assets/images/location/image-7.png'],
    price: 7500,
    currencySymbol: '₹',
    averageRating: 4.8,
    reviewCount: 88,
    isActive: true,
    curated: true,
    createdAt: new Date().toISOString(),
  },
  {
    _id: 'stay-8',
    name: 'Shimla Heritage Hotel',
    title: 'Colonial Heritage Hotel in Shimla',
    slug: 'shimla-heritage-hotel',
    code: 'STAY-008',
    type: 'stay_experience',
    category: {
      title: 'Heritage Hotel',
      description: 'Colonial era hotel',
      _id: 'cat-stay-8',
    },
    location: {
      _id: 'loc-stay-8',
      name: 'Shimla',
      state: 'Himachal Pradesh',
      city: 'Shimla',
    },
    imageUrl: ['/assets/images/location/image-8.png'],
    price: 11000,
    currencySymbol: '₹',
    averageRating: 4.6,
    reviewCount: 76,
    isActive: true,
    curated: true,
    createdAt: new Date().toISOString(),
  },
  {
    _id: 'stay-9',
    name: 'Alleppey Houseboat',
    title: 'Traditional Houseboat Stay in Alleppey Backwaters',
    slug: 'alleppey-houseboat',
    code: 'STAY-009',
    type: 'stay_experience',
    category: {
      title: 'Houseboat',
      description: 'Backwaters houseboat',
      _id: 'cat-stay-9',
    },
    location: {
      _id: 'loc-stay-9',
      name: 'Alleppey',
      state: 'Kerala',
      city: 'Alleppey',
    },
    imageUrl: ['/assets/images/location/image-9.png'],
    price: 13000,
    currencySymbol: '₹',
    averageRating: 4.9,
    reviewCount: 102,
    isActive: true,
    curated: true,
    createdAt: new Date().toISOString(),
  },
  {
    _id: 'stay-10',
    name: 'Darjeeling Tea Bungalow',
    title: 'Historic Tea Bungalow in Darjeeling Hills',
    slug: 'darjeeling-tea-bungalow',
    code: 'STAY-010',
    type: 'stay_experience',
    category: {
      title: 'Tea Bungalow',
      description: 'Heritage tea estate',
      _id: 'cat-stay-10',
    },
    location: {
      _id: 'loc-stay-10',
      name: 'Darjeeling',
      state: 'West Bengal',
      city: 'Darjeeling',
    },
    imageUrl: ['/assets/images/location/image-10.png'],
    price: 10000,
    currencySymbol: '₹',
    averageRating: 4.7,
    reviewCount: 89,
    isActive: true,
    curated: true,
    createdAt: new Date().toISOString(),
  },
];

export function BoutiqueStaysSection() {
  // Fetch boutique stays using React Query
  const { data, isLoading, error } = useBoutiqueStays();

  // Ref for scrollable container
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const isDraggingRef = React.useRef(false);
  const startXRef = React.useRef(0);
  const scrollLeftRef = React.useRef(0);
  const hasDraggedRef = React.useRef(false);
  const shouldPreventClickRef = React.useRef(false);

  // Debug logging
  React.useEffect(() => {
    console.log('=== BoutiqueStays Debug ===');
    console.log('isLoading:', isLoading);
    console.log('data:', data);
    console.log('data length:', data?.length);
    console.log('error:', error);
    console.log('=============================');
  }, [data, isLoading, error]);

  // Only use API data, don't show fallback
  // Hide section if no API data
  const hasApiData = data && Array.isArray(data) && data.length > 0;
  const allStays = hasApiData ? data : [];
  const stays = allStays.slice(0, 4); // Show only 4 items per Figma

  // Enable mouse drag scrolling
  React.useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || isLoading) return;

    const handleMouseDown = (e: MouseEvent) => {
      // Check if container has horizontal overflow
      const hasHorizontalScroll = container.scrollWidth > container.clientWidth;
      if (!hasHorizontalScroll) {
        return;
      }

      // Allow dragging even on links, but we'll prevent navigation if dragged
      isDraggingRef.current = true;
      hasDraggedRef.current = false;
      const rect = container.getBoundingClientRect();
      startXRef.current = e.clientX - rect.left;
      scrollLeftRef.current = container.scrollLeft;

      container.style.cursor = 'grabbing';
      container.style.userSelect = 'none';

      // Prevent default to avoid text selection and link navigation
      e.preventDefault();
      e.stopPropagation();
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!isDraggingRef.current) return;

      e.preventDefault();
      e.stopPropagation();

      const rect = container.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const walk = x - startXRef.current; // Distance moved

      // Only mark as dragged if movement is significant (more than 5px)
      if (Math.abs(walk) > 5) {
        hasDraggedRef.current = true;
        shouldPreventClickRef.current = true;
      }

      // Update scroll position
      // Dragging right (positive walk) should scroll right (increase scrollLeft)
      // Dragging left (negative walk) should scroll left (decrease scrollLeft)
      const newScrollLeft = scrollLeftRef.current - walk;
      container.scrollLeft = newScrollLeft;
    };

    const handleMouseUp = () => {
      isDraggingRef.current = false;

      if (container) {
        container.style.cursor = 'grab';
        container.style.userSelect = '';
      }

      // Reset hasDragged after a short delay to allow click handler to check it
      if (hasDraggedRef.current) {
        // Keep the flag for a bit to prevent click
        setTimeout(() => {
          hasDraggedRef.current = false;
          shouldPreventClickRef.current = false;
        }, 200);
      } else {
        // No drag occurred, allow click
        shouldPreventClickRef.current = false;
        hasDraggedRef.current = false;
      }
    };

    const handleMouseLeave = () => {
      isDraggingRef.current = false;
      hasDraggedRef.current = false;
      shouldPreventClickRef.current = false;
      if (container) {
        container.style.cursor = 'grab';
        container.style.userSelect = '';
      }
    };

    const handleClick = (e: MouseEvent) => {
      // Prevent navigation if we dragged
      if (shouldPreventClickRef.current || hasDraggedRef.current) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        return false;
      }
    };

    container.addEventListener('mousedown', handleMouseDown, {
      passive: false,
    });
    document.addEventListener('mousemove', handleMouseMove, { passive: false });
    document.addEventListener('mouseup', handleMouseUp);
    container.addEventListener('mouseleave', handleMouseLeave);
    // Use capture phase to intercept clicks before they reach links
    container.addEventListener('click', handleClick, { capture: true });

    return () => {
      container.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      container.removeEventListener('mouseleave', handleMouseLeave);
      container.removeEventListener('click', handleClick, { capture: true });
    };
  }, [isLoading, stays.length]);

  console.log('BoutiqueStays - Final stays count:', stays.length);
  console.log(
    'BoutiqueStays - Using:',
    data && data.length > 0 ? 'API DATA' : 'FALLBACK'
  );

  // Hide section if no API data
  if (!hasApiData && !isLoading) {
    return null;
  }

  return (
    <ScrollReveal
      as='section'
      className='px-4 pt-8 pb-4 md:px-20 md:pt-20 md:pb-2'
      style={{
        background:
          'linear-gradient(to bottom, #ffffff 0%, #fdf8ec 54.327%, #ffffff 100%)',
      }}
      direction='up'
      amount={0.25}
      margin='0px 0px -120px 0px'
    >
      <ScrollReveal
        className='max-w-[1401px] mx-auto'
        delay={0.1}
        direction='none'
      >
        {/* Header */}
        <ScrollReveal
          className='flex flex-col items-center w-full md:w-[744px] mx-auto'
          style={{
            gap: spacing[2], // 8px
            marginBottom: spacing[8], // 32px
          }}
          delay={0.2}
          direction='up'
        >
          <div
            className='flex items-center justify-center shrink-0'
            style={{
              gap: '10px',
              paddingLeft: spacing[1],
              paddingRight: spacing[3],
              paddingTop: spacing[1],
              paddingBottom: spacing[1],
            }}
          >
            <div
              className='flex items-center justify-center shrink-0 border border-black/12'
              style={{
                backgroundColor: '#fde047',
                padding: spacing[2],
                borderRadius: '9999px',
                width: '36px',
                height: '36px',
              }}
            >
              <Image
                src='/assets/images/icons/landing-page/boutique_stays.svg'
                alt='Nexgo Spaces'
                width={20}
                height={20}
                unoptimized
              />
            </div>
            <p
              className='shrink-0'
              style={{
                fontFamily: fontPrimary,
                fontWeight: 700,
                fontSize: '16px',
                lineHeight: 'normal',
                color: typographyColors.primary,
              }}
            >
              Nexgo Spaces
            </p>
          </div>

          <h2
            className='
    text-center whitespace-pre-wrap
    text-[24px] leading-[32px]
    md:text-[48px] md:leading-[52px]
  '
            style={{
              fontFamily: fontPrimary,
              fontWeight: 600, // semibold
              color: typographyColors.primary,
              minWidth: '100%',
            }}
          >
            Handpicked stays which has a charm, character, and connection to the
            destination.
          </h2>

          <p
            className='text-center whitespace-pre-wrap text-sm md:text-xl'
            style={{
              fontFamily: fontPrimary,
              fontWeight: 400,
              lineHeight: 'normal',
              color: typographyColors.primary,
              minWidth: '100%',
            }}
          >
            Stay where every moment is felt
          </p>
        </ScrollReveal>

        {/* Stay Cards - Horizontal Scrolling */}
        <div
          style={{
            marginTop: spacing[8], // 32px gap from header
            marginBottom: spacing[8], // 32px gap to explore link
            overflow: 'hidden', // Prevent parent from expanding
          }}
        >
          <div
            ref={scrollContainerRef}
            className='flex flex-nowrap overflow-x-auto overflow-y-clip relative scrollbar-hide'
            style={{
              gap: spacing[6], // 24px gap between cards per Figma
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              WebkitOverflowScrolling: 'touch',
              scrollBehavior: 'auto', // Use 'auto' for instant scroll during drag
              overflowX: 'auto', // Explicitly set horizontal overflow
              overflowY: 'hidden', // Prevent vertical overflow
              cursor: 'grab', // Show grab cursor to indicate draggable
              display: 'flex',
              flexDirection: 'row',
              width: '100%',
              paddingLeft: spacing[4], // 16px on mobile
              paddingRight: spacing[4], // 16px on mobile
            }}
          >
            {isLoading ? (
              // Loading skeleton - 4 items
              <>
                {[1, 2, 3, 4].map(i => (
                  <div
                    key={i}
                    className='flex-shrink-0 animate-pulse rounded-xl overflow-hidden'
                    style={{
                      width: 'min(calc(100vw - 80px), 318px)', // Responsive: account for section + container padding
                      minWidth: '280px', // Minimum width for mobile
                      height: '548px',
                      backgroundColor: backgroundColors.secondary,
                    }}
                  />
                ))}
              </>
            ) : (
              stays.map(stay => (
                <div key={stay._id} className='flex-shrink-0'>
                  <StayCard experience={stay} />
                </div>
              ))
            )}
          </div>
        </div>

        {/* Explore Link */}
        <ScrollReveal
          className='flex items-center justify-center'
          style={{ gap: spacing[1.5] }} // 6px
          delay={0.45}
          direction='none'
        >
          <Link
            href='/experiences?type=stays'
            className='cursor-pointer hover:opacity-80 transition-opacity underline'
            style={{
              fontFamily: fontPrimary,
              fontWeight: 700,
              fontSize: '16px',
              lineHeight: 'normal',
              color: typographyColors.primary,
              textUnderlinePosition: 'from-font',
              textDecorationLine: 'underline',
            }}
          >
            Explore All Nexgo Spaces
          </Link>
          <svg
            width='20'
            height='20'
            viewBox='0 0 20 20'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <path
              fillRule='evenodd'
              d='M5.22 14.78a.75.75 0 001.06 0l7.22-7.22v5.69a.75.75 0 001.5 0v-7.5a.75.75 0 00-.75-.75h-7.5a.75.75 0 000 1.5h5.69l-7.22 7.22a.75.75 0 000 1.06z'
              clipRule='evenodd'
              fill='#27272a'
            />
          </svg>
        </ScrollReveal>
      </ScrollReveal>
    </ScrollReveal>
  );
}

interface StayCardProps {
  experience: Experience;
}

function StayCard({ experience }: StayCardProps) {
  const { _id, title, location, shortTitle, name, price, currencySymbol } =
    experience;

  // Log the _id being used for this card
  React.useEffect(() => {
    console.log('StayCard - Using _id:', {
      _id,
      name: experience.name,
      title: experience.title,
      isFallbackId: _id?.startsWith('stay-'),
      stayDetailUrl: _id ? `/stay-experience-details/${_id}` : '#',
    });
  }, [_id, experience.name, experience.title]);

  // Get image from media array, with fallback to imageUrl
  const displayImage = getExperienceImage(experience);

  // Use location name
  const displayLocation = location
    ? `${location.city || location.name}, ${location.state}`
    : 'Location TBD';

  // Use shortTitle if available, otherwise title or name
  const displayTitle = shortTitle || title || name;

  // Infer backend currency from currencySymbol (₹ = INR, $ = USD, etc.)
  // Defaults to USD if currencySymbol doesn't match known symbols
  const getBackendCurrency = (symbol?: string | null): string => {
    if (!symbol) return 'USD';
    if (symbol === '₹' || symbol === 'INR') return 'INR';
    if (symbol === '$' || symbol === 'USD') return 'USD';
    // Default to USD for unknown symbols
    return 'USD';
  };

  const backendCurrency = getBackendCurrency(currencySymbol);
  const { currencySymbol: userCurrencySymbol, currencyCode } = useCurrency();
  const backendCurrencyCode = getCurrencyCode(backendCurrency);
  const convertedAmount = useConvertedPrice(price || 0, backendCurrencyCode);

  // Smart conversion: only use converted value if currencies don't match
  const finalAmount =
    backendCurrencyCode !== currencyCode ? convertedAmount : price || 0;

  // Format price - split into amount and "person" text
  const priceAmount = price
    ? Math.round(finalAmount).toLocaleString()
    : '15,000';

  // Use _id for stay experience details
  const stayDetailUrl = _id ? `/stay-experience-details/${_id}` : '#';

  return (
    <Link href={stayDetailUrl}>
      <div
        className='relative rounded-xl overflow-hidden cursor-pointer hover:opacity-90 transition-opacity flex flex-col justify-end'
        style={{
          width: 'min(calc(100vw - 80px), 318px)', // Responsive: account for section + container padding
          minWidth: '280px', // Minimum width for mobile
          height: '548px',
        }}
      >
        {/* Background Image */}
        <Image
          src={displayImage}
          alt={displayTitle}
          fill
          className='object-cover'
        />

        {/* Gradient Overlay */}
        <div
          className='absolute inset-0 rounded-xl'
          style={{
            background:
              'linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0) 55.288%, #000000 100%)',
          }}
        />

        {/* Content */}
        <div
          className='relative flex flex-col'
          style={{
            gap: spacing[3], // 12px per Figma
            paddingLeft: spacing[4], // 16px
            paddingRight: spacing[4],
            paddingBottom: spacing[6], // 24px
          }}
        >
          {/* Title */}
          <h3
            className='whitespace-pre-wrap'
            style={{
              fontFamily: fontPrimary,
              fontWeight: 700,
              fontSize: '24px',
              lineHeight: 'normal',
              color: '#FFF',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {displayTitle}
          </h3>

          {/* Location */}
          <p
            style={{
              fontFamily: fontPrimary,
              fontWeight: 500,
              fontSize: '16px',
              lineHeight: 'normal',
              color: 'white',
            }}
          >
            {displayLocation}
          </p>

          {/* Divider */}
          <div
            style={{
              height: '1px',
              background: 'rgba(255, 255, 255, 0.80)',
            }}
          />

          {/* Price Container */}
          <div className='flex items-center justify-between'>
            {/* Price Info */}
            <div className='flex items-center' style={{ gap: '0px' }}>
              {/* Currency Symbol */}
              <div
                className='flex items-center justify-center shrink-0'
                style={{
                  width: '20px',
                  height: '20px',
                  color: 'white',
                  fontFamily: fontPrimary,
                  fontSize: '16px',
                  lineHeight: 'normal',
                }}
              >
                {userCurrencySymbol || '₹'}
              </div>

              {/* Price Text */}
              <div
                className='flex flex-col justify-center whitespace-nowrap'
                style={{
                  fontFamily: fontPrimary,
                  lineHeight: '0',
                  alignItems: 'flex-start', // Align items to start for better vertical centering
                }}
              >
                <p
                  style={{
                    lineHeight: 'normal',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <span
                    style={{
                      fontFamily: fontPrimary,
                      fontWeight: 500,
                      fontSize: '18px',
                      color: '#FFF',
                      lineHeight: 'normal',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    {priceAmount}
                  </span>
                  <span
                    style={{
                      fontWeight: 500,
                      fontSize: '16px',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    {' '}
                  </span>
                  <span
                    style={{
                      fontFamily: fontPrimary,
                      fontWeight: 500,
                      fontSize: '16px',
                      color: '#FFF',
                      lineHeight: 'normal',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    &nbsp; /
                  </span>
                  <span
                    style={{
                      fontWeight: 500,
                      fontSize: '16px',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    {' '}
                  </span>
                  <span
                    style={{
                      fontFamily: fontPrimary,
                      fontWeight: 300,
                      fontSize: '10px',
                      color: '#FFF',
                      lineHeight: 'normal',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    &nbsp;person
                  </span>
                </p>
              </div>
            </div>

            {/* Arrow Icon */}
            <svg
              width='20'
              height='20'
              viewBox='0 0 20 20'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                fillRule='evenodd'
                d='M5.22 14.78a.75.75 0 001.06 0l7.22-7.22v5.69a.75.75 0 001.5 0v-7.5a.75.75 0 00-.75-.75h-7.5a.75.75 0 000 1.5h5.69l-7.22 7.22a.75.75 0 000 1.06z'
                clipRule='evenodd'
                fill='white'
              />
            </svg>
          </div>
        </div>
      </div>
    </Link>
  );
}
